/**
 * MathBlock — display-mode KaTeX renderer for category-theoretic
 * statements, commutative diagrams, and theorem statements with
 * universal quantifiers.
 *
 * Wraps `katex.renderToString` directly rather than `react-katex` so the
 * component avoids the synthetic React 18 / 19 peer-dep warnings that
 * `react-katex` still ships with on its 3.x release line. KaTeX itself
 * is server-renderable and produces deterministic markup; the emitted
 * string is still routed through the shared `TrustedHtml` boundary so
 * the raw HTML sink is centralized and audited.
 *
 * Theme tokens (background / foreground / accent) come from
 * `tokens.data.json` via the design-system module so a tokens recolour
 * propagates here without a code change.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import katex, { type ParseError } from "katex";
import React, { useMemo } from "react";
import { GRAY, PRIMARY } from "../../design-system/tokens.js";
import { TrustedHtml } from "../TrustedHtml.js";
import { createTrustedHtml, type TrustedHtmlValue } from "../../security/trustedHtml.js";

export interface MathBlockProps {
  /** LaTeX source rendered in display mode. */
  readonly children: string;
  /**
   * Optional caption shown below the rendered expression. Useful for
   * theorem labels (`Naturality square (PERMIT)`) or witness ids.
   */
  readonly caption?: React.ReactNode;
  /**
   * Accessible label announced by assistive tech. Falls back to the
   * literal LaTeX source so screen readers always have a verbal
   * description even when MathML is suppressed.
   */
  readonly ariaLabel?: string;
  /** Optional inline style overrides applied to the wrapper. */
  readonly style?: React.CSSProperties;
}

interface RenderResult {
  readonly html?: TrustedHtmlValue;
  readonly error?: string;
}

function renderMath(source: string, displayMode: boolean): RenderResult {
  try {
    const html = katex.renderToString(source, {
      displayMode,
      throwOnError: false,
      strict: "ignore",
      output: "html",
      trust: false,
    });
    return { html: createTrustedHtml(html, "katex") };
  } catch (error) {
    const message = (error as ParseError | Error).message;
    return { error: message };
  }
}

/** Display-mode KaTeX block. */
export const MathBlock = React.memo(function MathBlock({
  children,
  caption,
  ariaLabel,
  style,
}: MathBlockProps): React.JSX.Element {
  const { html, error } = useMemo(() => renderMath(children, true), [children]);
  return (
    <figure
      role="math"
      aria-label={ariaLabel ?? children}
      data-csk-math="block"
      style={{
        backgroundColor: GRAY[50],
        border: `1px solid ${GRAY[300]}`,
        borderRadius: "0.5rem",
        color: GRAY[900],
        margin: 0,
        overflowX: "auto",
        padding: "1rem 1.25rem",
        ...style,
      }}
    >
      {html === undefined ? (
        <pre style={{ margin: 0 }}>{children}</pre>
      ) : (
        <TrustedHtml html={html} />
      )}
      {caption !== undefined && (
        <figcaption
          style={{
            color: GRAY[600],
            fontSize: "0.8125rem",
            marginTop: "0.5rem",
            textAlign: "center",
          }}
        >
          {caption}
        </figcaption>
      )}
      {error !== undefined && (
        <div
          role="status"
          style={{ color: PRIMARY[700], fontSize: "0.75rem", marginTop: "0.5rem" }}
        >
          {error}
        </div>
      )}
    </figure>
  );
});
